import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  shouldOpenCheckInFromLaunch,
  useCheckInLauncher,
} from "@/features/home/onboarding/use-check-in-launcher";
import {
  type CheckInCtaState,
  deriveCheckInCtaState,
} from "@/features/pulse-surveys/components/check-in-cta/check-in-cta-state";
import { MoodOrb } from "@/features/pulse-surveys/components/check-in-cta/mood-orb";
import { SelfCheckInDialog } from "@/features/pulse-surveys/components/self-check-in-dialog";
import { useSelfCheckInOverview } from "@/features/pulse-surveys/hooks/use-self-check-in";
import { Card, CardContent } from "@/shared/components/ui/card";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { cn } from "@/shared/lib/cn";
import { MOOD_AFFIRMATIONS, MOOD_DISPLAY } from "@/shared/pulse/mood/mood-display";

interface Props {
  workspaceId: string;
  /// "topbar": the always-visible orb button in the app shell.
  /// "hero": the celebration card on My surveys, rendered only once today's
  /// check-in is done (open states live in the "To answer" list row there).
  mount: "topbar" | "hero";
}

/// Container for the daily check-in CTA. Owns the overview query, the
/// onboarding launcher token handling, the dialog, and the one-shot
/// completion celebration; the visual (MoodOrb) stays swappable behind
/// `CheckInCtaState`.
export function CheckInCta({ workspaceId, mount }: Props): ReactNode {
  const { workspace } = useActiveWorkspace();
  const { requestToken, setCheckInOpen } = useCheckInLauncher();
  const overview = useSelfCheckInOverview(workspaceId);
  const data = overview.data;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [handledToken, setHandledToken] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const prevAvailability = useRef<string | null>(null);

  const state = deriveCheckInCtaState({
    isReadOnly: Boolean(workspace.isReadOnly),
    isLoading: overview.isLoading,
    overview: data,
  });

  // Report the dialog's open state up to the launcher so the onboarding hub
  // modal can step aside while a check-in is in progress.
  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      setCheckInOpen(open);
    },
    [setCheckInOpen],
  );

  useEffect(() => {
    if (
      !shouldOpenCheckInFromLaunch({
        requestToken,
        handledToken,
        isReadOnly: Boolean(workspace.isReadOnly),
        isOverviewLoading: overview.isLoading,
        hasOverview: Boolean(data),
        availability: data?.availability ?? null,
      })
    ) {
      return;
    }

    setHandledToken(requestToken);
    setDialogOpen(true);
    setCheckInOpen(true);
  }, [requestToken, handledToken, workspace.isReadOnly, overview.isLoading, data, setCheckInOpen]);

  // One-shot burst when the availability flips to done while mounted (i.e.
  // the member just finished their check-in, not a page load onto done).
  const availability = data?.availability ?? null;
  useEffect(() => {
    const prev = prevAvailability.current;
    prevAvailability.current = availability;
    if (availability === "done_today" && prev !== null && prev !== "done_today") {
      setCelebrating(true);
      const timer = setTimeout(() => setCelebrating(false), 900);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [availability]);

  if (mount === "hero") {
    return <HeroDoneCard state={state} celebrating={celebrating} />;
  }

  const openable = (state.kind === "ready" || state.kind === "in_progress") && data;
  if (openable) {
    // "Daily check-in", not "Check in". This is the mood survey, and its own
    // dialog already calls itself that. Bare "Check in" put an identical label
    // in the topbar to the ATTENDANCE check-in button on My attendance, an
    // unrelated action about a thousand pixels away on the same screen.
    const label = state.kind === "in_progress" ? "Resume check-in" : "Daily check-in";
    return (
      <SelfCheckInDialog
        workspaceId={workspaceId}
        overview={data}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
      >
        <button
          type="button"
          aria-label={label}
          title="Daily check-in. Thirty seconds, always anonymous."
          className={cn(
            "group inline-flex h-9 items-center gap-2 rounded-full px-2.5 text-sm font-medium text-text-secondary transition-colors",
            "hover:bg-surface-muted hover:text-text-primary",
            "focus-visible:outline-none focus-visible:shadow-ring-focus",
          )}
        >
          <span className="group-hover:animate-orb-wobble">
            <MoodOrb state={state} />
          </span>
          {label}
        </button>
      </SelfCheckInDialog>
    );
  }

  const doneMood = state.kind === "done" ? state.mood : null;
  const streak = state.kind === "done" ? state.streak : null;
  return (
    <span
      role="status"
      title={statusTitle(state)}
      className="inline-flex h-9 items-center gap-2 px-2.5 text-sm font-medium text-text-tertiary"
    >
      <MoodOrb state={state} celebrating={celebrating} />
      {state.kind === "done" ? (
        <>
          <span className="sr-only">Checked in today.</span>
          <span aria-hidden="true">{doneMood ? MOOD_DISPLAY[doneMood].label : "Checked in"}</span>
          {streak !== null && streak >= 2 ? (
            <span className="inline-flex h-5 items-center rounded-full bg-violet-50 px-2 text-3xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800">
              day {streak}
            </span>
          ) : null}
        </>
      ) : (
        <span className="sr-only">{statusTitle(state)}</span>
      )}
    </span>
  );
}

function statusTitle(state: CheckInCtaState): string {
  switch (state.kind) {
    case "read_only":
      return "Check-in unavailable in this read-only sample";
    case "loading":
      return "Loading check-in";
    case "done":
      return "You've checked in today. See you tomorrow.";
    default:
      return "Check-in unavailable right now";
  }
}

/// The My surveys hero: appears only after today's check-in, closing the
/// habit loop with the day's mood, an affirmation, and the streak.
function HeroDoneCard({
  state,
  celebrating,
}: {
  state: CheckInCtaState;
  celebrating: boolean;
}): ReactNode {
  if (state.kind !== "done") return null;
  const affirmation = state.mood
    ? MOOD_AFFIRMATIONS[state.mood]
    : "Thanks for checking in. See you tomorrow.";
  return (
    <Card className="border-0 bg-pulse-soft shadow-xs ring-1 ring-inset ring-border-base">
      <CardContent className="flex items-center gap-3.5 py-4">
        <MoodOrb state={state} celebrating={celebrating} className="size-9 text-xl" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary">
            Checked in
            {state.mood ? `, feeling ${MOOD_DISPLAY[state.mood].label.toLowerCase()}` : ""}
            {state.streak !== null && state.streak >= 2 ? (
              <span className="ml-2 inline-flex h-5 items-center rounded-full bg-violet-50 px-2 align-middle text-3xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800">
                {state.streak} day streak
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 text-sm text-text-tertiary">{affirmation}</p>
        </div>
      </CardContent>
    </Card>
  );
}
